import type { AnalyticsProperties } from "analytics/AnalyticsContext";

/** What a quote-flow event is stamped with besides the caller's own
 * properties; read at call time so the latest scope always wins. */
interface QuoteFlowStamp {
  quote_id: string;
  quote_type: string;
  credential: string;
  quote_origin: string;
  grant_channel: string;
  grant_origin: string;
}

interface QuoteFlowTrackerOptions {
  send(event: string, properties: AnalyticsProperties): void;
  stamp(): QuoteFlowStamp;
}

/**
 * Stamps every quote-flow event with its scope and the station on screen.
 * The station is whatever `setStep` or `trackAtStep` last named; `track`
 * reads it at call time.
 */
export function createQuoteFlowTracker<Step extends string>({
  send,
  stamp,
}: QuoteFlowTrackerOptions) {
  let step: Step | null = null;

  const track = (event: string, properties?: AnalyticsProperties) => {
    send(event, { ...properties, ...stamp(), step });
  };

  const setStep = (next: Step | null) => {
    step = next;
  };

  /** Names the station and reports the event under it in one move, for a
   * face that renders before the URL (and so the flow's own `setStep`)
   * has caught up with it. */
  const trackAtStep = (
    next: Step,
    event: string,
    properties?: AnalyticsProperties,
  ) => {
    setStep(next);
    track(event, properties);
  };

  return { track, setStep, trackAtStep };
}
